import { Component, OnInit, OnDestroy, Inject, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SignalRService, MeetingMessage } from 'src/app/shared/services/signalr.service';
import { UserService } from 'src/app/shared/services/user.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-meeting-session',
  templateUrl: './meeting-session.component.html',
  styleUrls: ['./meeting-session.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeetingSessionComponent implements OnInit, OnDestroy {
  meetingId: string = '';
  meetingTitle: string = '';
  meetingData: any = null;
  startedBy: string = '';
  
  messages: MeetingMessage[] = [];
  connectedUsers: string[] = [];
  newMessage: string = '';
  currentUserId: string = '';
  currentUserName: string = '';
  
  private destroy$ = new Subject<void>();
  private cdr = inject(ChangeDetectorRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<MeetingSessionComponent>,
    private signalRService: SignalRService,
    private userService: UserService
  ) {
    this.meetingId = data?.meetingId || '';
    this.meetingTitle = data?.meetingTitle || 'Meeting Session';
    this.meetingData = data?.meetingData || null;
    this.startedBy = data?.startedBy || '';
    
    this.currentUserId = this.userService.user?.Id || '';
    this.currentUserName = this.userService.user?.FullName || 'User';
  }

  ngOnInit(): void {
    // Join the meeting session
    this.signalRService.joinMeeting(
      this.meetingId,
      this.currentUserId,
      this.currentUserName
    );

    // Subscribe to meeting messages
    this.signalRService.meetingMessage$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (message: MeetingMessage) => {
        if (message.userId !== this.currentUserId) {
          this.messages = [...this.messages, message];
          this.cdr.markForCheck();
        }
      }
    );

    // Subscribe to user joined events
    this.signalRService.userJoined$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (data: { userId: string; userName: string }) => {
        if (data.userId !== this.currentUserId) {
          this.connectedUsers = [...this.connectedUsers, data.userName];
          this.addSystemMessage(`${data.userName} joined the meeting`);
          this.cdr.markForCheck();
        }
      }
    );

    // Subscribe to user left events
    this.signalRService.userLeft$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (data: { userId: string }) => {
        this.addSystemMessage('A user left the meeting');
        this.cdr.markForCheck();
      }
    );
  }

  ngOnDestroy(): void {
    // Leave the meeting session
    this.signalRService.leaveMeeting(this.meetingId, this.currentUserId);
    
    // Unsubscribe from all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  sendMessage(): void {
    if (!this.newMessage.trim()) return;

    const message: MeetingMessage = {
      userId: this.currentUserId,
      userName: this.currentUserName,
      message: this.newMessage.trim(),
      timestamp: new Date().toISOString()
    };

    this.messages = [...this.messages, message];
    this.signalRService.sendMessageToMeeting(
      this.meetingId,
      this.currentUserId,
      this.currentUserName,
      message.message
    );

    this.newMessage = '';
    this.cdr.markForCheck();
  }

  addSystemMessage(message: string): void {
    this.messages = [...this.messages, {
      userId: 'system',
      userName: 'System',
      message: message,
      timestamp: new Date().toISOString()
    }];
    this.cdr.markForCheck();
  }
  
  // TrackBy function for messages
  trackByMessageId(index: number, message: MeetingMessage): string {
    return message.timestamp + message.userId;
  }
  
  // TrackBy function for users
  trackByUserName(index: number, userName: string): string {
    return userName;
  }

  closeSession(): void {
    this.dialogRef.close();
  }
}

